import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { Router } from 'vue-router'

import { loadAdminApplicationData } from '@/api/admin/application'
import type {
  AdminDictionaryItem,
  AdminMenuRouteSource
} from '@/shared/types/admin'
import {
  registerAdminDynamicRoutes,
  type AdminRouteRegistration
} from '@/router/admin-dynamic-routes'

import { useAdminSessionStore } from './session'

export const useAdminApplicationStore = defineStore('admin-application', () => {
  const menus = ref<AdminMenuRouteSource[]>([])
  const permissions = ref<string[]>([])
  const dictionaries = ref<AdminDictionaryItem[]>([])
  const routeRegistrations = ref<AdminRouteRegistration[]>([])
  const initialized = ref(false)
  const initializing = ref(false)
  const initializationError = ref('')
  let pendingInitialization: Promise<void> | null = null

  const reset = (): void => {
    menus.value = []
    permissions.value = []
    dictionaries.value = []
    routeRegistrations.value = []
    initialized.value = false
    initializing.value = false
    initializationError.value = ''
    pendingInitialization = null
  }

  const initialize = (router: Router, force = false): Promise<void> => {
    if (initialized.value && !force) {
      return Promise.resolve()
    }
    if (pendingInitialization && !force) {
      return pendingInitialization
    }

    initializing.value = true
    initializationError.value = ''
    pendingInitialization = loadAdminApplicationData()
      .then(data => {
        const registrations = registerAdminDynamicRoutes(router, data.menus)
        useAdminSessionStore().setUser(data.user)
        menus.value = data.menus
        permissions.value = data.permissions
        dictionaries.value = data.dictionaries
        routeRegistrations.value = registrations
        initialized.value = true
      })
      .catch((error: unknown) => {
        menus.value = []
        permissions.value = []
        dictionaries.value = []
        routeRegistrations.value = []
        initialized.value = false
        initializationError.value = error instanceof Error ? error.message : 'Admin 初始化失败'
        throw error
      })
      .finally(() => {
        initializing.value = false
        pendingInitialization = null
      })

    return pendingInitialization
  }

  return {
    menus,
    permissions,
    dictionaries,
    routeRegistrations,
    initialized,
    initializing,
    initializationError,
    initialize,
    reset
  }
})
